import {
  Button,
  Flex,
  InputRightAddon,
  List,
  ListItem,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { FileUpload } from '../../../../../components/shared/DragAndDrop/DropZone';
import InputSelector from '../../../../../components/shared/Inputs/InputSelector';
import InputText from '../../../../../components/shared/Inputs/InputText/index';
import backend from '../../../../../services/backend';

const MAX_TOTAL_FILES_SIZE = 7.5 * 1024 * 1024; // 7.5 MB

function obterTamanhoTotalArquivos(arquivos: File[]) {
  return arquivos.reduce((total, arquivo) => total + arquivo.size, 0);
}

function formatarTamanhoArquivo(tamanhoEmBytes: number) {
  return `${(tamanhoEmBytes / 1024 / 1024).toFixed(2)} MB`;
}

const arquivosSchema = z
  .array(z.instanceof(File))
  .min(1, 'Envie ao menos 1 arquivo')
  .refine(
    arquivos => obterTamanhoTotalArquivos(arquivos) <= MAX_TOTAL_FILES_SIZE,
    'O tamanho total dos arquivos nao pode ultrapassar 7,5 MB. Remova ou compacte algum arquivo para continuar.',
  );

const senhaEmissaoSchema = z
  .string()
  .refine(
    senha => senha === '' || senha.length >= 8,
    'A senha deve ter no mínimo 8 caracteres.',
  )
  .refine(
    senha => senha === '' || /\d/.test(senha),
    'A senha deve conter pelo menos um número.',
  )
  .refine(
    senha => senha === '' || /[A-Z]/.test(senha),
    'A senha deve conter pelo menos uma letra maiúscula.',
  )
  .refine(
    senha => senha === '' || /[^A-Za-z0-9\s]/.test(senha),
    'A senha deve conter pelo menos um caractere especial.',
  );

const adicionaRegraConfirmacao = (schema: z.ZodObject<any>) =>
  schema.superRefine((dados, contexto) => {
    if (dados.senha_emissao !== dados.confirmar_senha) {
      contexto.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['confirmar_senha'],
        message: 'A confirmação deve ser igual à senha.',
      });
    }
  });

export const FormPessoaFisica = adicionaRegraConfirmacao(z.object({
  documento: z.string().nonempty('Campo Obrigatório'),
  cei_caepf: z.string().max(14).optional(),
  razao_nome: z.string().optional(),
  telefone: z.string().optional(),
  celular: z.string().nonempty('Campo Obrigatório'),
  email: z.string().email('Email Inválido').nonempty('Campo Obrigatório'),
  email_contador: z
    .string()
    .email('Email Inválido')
    .optional()
    .or(z.literal('')),
  senha_emissao: senhaEmissaoSchema,
  confirmar_senha: z.string(),
  cep: z.string().nonempty('Campo Obrigatório'),
  logradouro: z.string().nonempty('Campo Obrigatório'),
  numero: z.string().nonempty('Campo Obrigatório'),
  bairro: z.string().nonempty('Campo Obrigatório'),
  uf: z.string().nonempty('Campo Obrigatório'),
  cidade: z.string().nonempty('Campo Obrigatório'),
  ibge: z.string().nonempty('Campo Obrigatório'),
  complemento: z.string().optional(),
  arquivos: arquivosSchema,
}));

export const FormPessoaJuridica = adicionaRegraConfirmacao(z.object({
  documento: z.string().nonempty('Campo Obrigatório'),
  razao_nome: z.string().nonempty('Campo Obrigatório'),
  fantasia: z.string().optional(),
  representante: z.string().nonempty('Campo Obrigatório'),
  cpf_representante: z.string().nonempty('Campo Obrigatório'),
  telefone: z.string().optional(),
  celular: z.string().nonempty('Campo Obrigatório'),
  email: z.string().email('Email Inválido').nonempty('Campo Obrigatório'),
  email_contador: z
    .string()
    .email('Email Inválido')
    .optional()
    .or(z.literal('')),
  senha_emissao: senhaEmissaoSchema,
  confirmar_senha: z.string(),
  cep: z.string().nonempty('Campo Obrigatório'),
  logradouro: z.string().nonempty('Campo Obrigatório'),
  numero: z.string().nonempty('Campo Obrigatório'),
  bairro: z.string().nonempty('Campo Obrigatório'),
  uf: z.string().nonempty('Campo Obrigatório'),
  cidade: z.string().nonempty('Campo Obrigatório'),
  ibge: z.string().nonempty('Campo Obrigatório'),
  complemento: z.string().optional(),
  arquivos: arquivosSchema,
}));

interface IDadosCertificado {
  certificadoData: any;
  setCertificadoData(data: any): void;
  handleMudaStep(novoStep: number): void;
  tipoPessoa?: string | null;
}

const DadosCertificado: React.FC<IDadosCertificado> = ({
  certificadoData,
  setCertificadoData,
  handleMudaStep,
  tipoPessoa,
}) => {
  const Toast = useToast();
  const color = useColorModeValue('#1A202C', 'white');
  const details = useColorModeValue('gray.200', 'gray.700');

  const [buscandoCNPJ, setBuscandoCNPJ] = useState(false);
  const [buscandoCEP, setBuscandoCEP] = useState(false);
  const [mostrarSenhaEmissao, setMostrarSenhaEmissao] = useState(false);
  const [mostrarConfirmacaoSenha, setMostrarConfirmacaoSenha] = useState(false);

  const FormSchema = tipoPessoa === '1' ? FormPessoaFisica : FormPessoaJuridica;

  type formData = any;
  const methods = useForm<formData>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      documento: certificadoData.documento || '',
      razao_nome: certificadoData.razao_nome || '',
      fantasia: certificadoData.fantasia || '',
      representante: certificadoData.representante || '',
      cpf_representante: certificadoData.cpf_representante || '',
      telefone: certificadoData.telefone || '',
      celular: certificadoData.celular || '',
      email: certificadoData.email || '',
      email_contador: certificadoData.email_contador || '',
      senha_emissao: certificadoData.senha_emissao || '',
      confirmar_senha:
        certificadoData.confirmar_senha || certificadoData.senha_emissao || '',
      cep: certificadoData.cep || '',
      logradouro: certificadoData.logradouro || '',
      numero: certificadoData.numero || '',
      bairro: certificadoData.bairro || '',
      uf: certificadoData.uf || '',
      cidade: certificadoData.cidade || '',
      ibge: certificadoData.ibge || '',
      complemento: certificadoData.complemento || '',
      arquivos: certificadoData.arquivos || [],
    },
  });

  const {
    handleSubmit,
    reset,
    watch,
    register,
    setValue,
    control,
    formState: { errors },
  } = methods;

  const cnpjDigitado = watch('documento');
  const cepDigitado = watch('cep');

  const handleSalvaDados = async (data: formData) => {
    try {
      const totalArquivos = obterTamanhoTotalArquivos(data.arquivos || []);

      if (totalArquivos > MAX_TOTAL_FILES_SIZE) {
        Toast({
          title: 'Arquivos muito grandes',
          description:
            'O tamanho total dos arquivos nao pode ultrapassar 7,5 MB. Remova ou compacte algum arquivo para continuar.',
          isClosable: true,
          status: 'error',
          position: 'bottom-right',
        });

        return;
      }

      const dataFinal = {
        ...data,
        tipo_documento: tipoPessoa,
      };
      delete dataFinal.confirmar_senha;

      setCertificadoData(dataFinal);
      handleMudaStep(3);
    } catch (error) {
      //
    }
  };

  const buscaDadosCnpj = async () => {
    if (!cnpjDigitado) {
      Toast({
        title: 'Insira um CNPJ para buscar',
        isClosable: true,
        position: 'bottom-right',
      });
      return;
    }

    if (buscandoCNPJ) return;

    setBuscandoCNPJ(true);
    const cnpjLimpo = cnpjDigitado?.replace(/[^a-zA-Z0-9]/g, '');

    try {
      const response = await backend.get(`/cnpj/${cnpjLimpo}`);
      const dados = response?.data;

      setValue('razao_nome', dados?.nome);
      setValue('fantasia', dados?.fantasia);
      setValue('celular', dados?.telefone);
      setValue('telefone', dados?.telefone);
      setValue('email', dados?.email);
      setValue('ibge', dados?.ibge);
      setValue('cep', dados?.cep);
      setValue('logradouro', dados?.logradouro);
      setValue('numero', dados?.numero);
      setValue('bairro', dados?.bairro);
      setValue('uf', dados?.uf);
      setValue('cidade', dados?.municipio);
      setValue('complemento', dados?.complemento);
    } catch (error: any) {
      Toast({
        title: error?.response?.data?.message,
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
    } finally {
      setBuscandoCNPJ(false);
    }
  };

  const buscaDadosCep = async () => {
    if (!cepDigitado) {
      Toast({
        title: 'Insira um CEP para buscar',
        isClosable: true,
        position: 'bottom-right',
      });
      return;
    }

    if (buscandoCEP) return;

    setBuscandoCEP(true);
    const cepLimpo = cepDigitado?.replace(/[^0-9]/g, '');

    try {
      const response = await backend.get('/cep', {
        params: {
          cep: cepLimpo,
        },
      });

      const dados = response?.data;

      setValue('cep', dados?.cep);
      setValue('logradouro', dados?.logradouro);
      setValue('bairro', dados?.bairro);
      setValue('uf', dados?.uf);
      setValue('cidade', dados?.localidade ?? dados?.municipio);
      setValue('ibge', dados?.ibge);
      setValue('complemento', dados?.complemento);
    } catch (error: any) {
      Toast({
        title: error?.response?.data?.message ?? 'Erro ao buscar CEP',
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
    } finally {
      setBuscandoCEP(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        autoComplete="off"
        onSubmit={handleSubmit(handleSalvaDados, () => {
          Toast({
            title: 'Validação',
            description: 'Preencha os campos destacados.',
            status: 'error',
            duration: 4000,
            position: 'bottom-right',
            isClosable: true,
          });
        })}
      >
        <Flex justify="space-between" mx="8">
          <Button onClick={() => handleMudaStep(1)}>Voltar</Button>
          <Button type="submit">Avançar</Button>
        </Flex>

        <Flex w="100%" p="8" pt="2" flexDir="column">
          {tipoPessoa === '1' && (
            <Flex flexDir="column" w="100%">
              <SimpleGrid
                templateColumns={['1fr', '1fr', '1fr 1fr 1fr 2fr']}
                gap={4}
                justify="center"
                w="100%"
              >
                <InputSelector
                  label="Tipo"
                  labelColor={color}
                  value="1"
                  isDisabled
                  options={[
                    {
                      label: 'FISICA',
                      value: '1',
                    },
                  ]}
                />

                <Controller
                  control={control}
                  name="documento"
                  render={({ field }) => (
                    <InputText
                      label="CPF"
                      labelColor={color}
                      toUpperCase
                      mask="000.000.000-00"
                      maskOptions={{
                        '*': /[A-Za-z0-9]/,
                      }}
                      {...field}
                      error={errors.documento}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="cei_caepf"
                  render={({ field }) => (
                    <InputText
                      label="CEI/CAEPF"
                      labelColor={color}
                      maxLength={14}
                      {...field}
                      error={errors.razao_nome}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="razao_nome"
                  render={({ field }) => (
                    <InputText
                      label="Razão/Nome Completo"
                      labelColor={color}
                      {...field}
                      error={errors.razao_nome}
                    />
                  )}
                />
              </SimpleGrid>
            </Flex>
          )}

          {tipoPessoa === '2' && (
            <Flex flexDir="column" w="100%">
              <SimpleGrid
                templateColumns={['1fr', '1fr', '1fr 1fr 2fr']}
                gap={4}
                justify="center"
                w="100%"
              >
                <InputSelector
                  label="Tipo"
                  labelColor={color}
                  value="1"
                  isDisabled
                  options={[
                    {
                      label: 'JURIDICA',
                      value: '1',
                    },
                  ]}
                />

                <Controller
                  control={control}
                  name="documento"
                  render={({ field }) => (
                    <InputText
                      label="CNPJ"
                      labelColor={color}
                      toUpperCase
                      mask="**.***.***/****-00"
                      maskOptions={{
                        '*': /[A-Za-z0-9]/,
                      }}
                      {...field}
                      error={errors.documento}
                      borderLeftRadius="0"
                      rightIcon={<Search />}
                      inputRightAddon={
                        <InputRightAddon
                          cursor="pointer"
                          onClick={buscaDadosCnpj}
                        >
                          {buscandoCNPJ ? (
                            <Tooltip label="Buscando dados do CNPJ">
                              <Spinner color={color} />
                            </Tooltip>
                          ) : (
                            <Tooltip label="Buscar dados do CNPJ">
                              <Search size={24} color={color} />
                            </Tooltip>
                          )}
                        </InputRightAddon>
                      }
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="razao_nome"
                  render={({ field }) => (
                    <InputText
                      label="Razão/Nome Completo"
                      labelColor={color}
                      {...field}
                      error={errors.razao_nome}
                    />
                  )}
                />
              </SimpleGrid>

              <SimpleGrid
                templateColumns={['1fr', '1fr', '2fr 1fr 1fr']}
                gap={4}
                justify="center"
                w="100%"
                mt="1"
              >
                <Controller
                  control={control}
                  name="fantasia"
                  render={({ field }) => (
                    <InputText
                      label="Fantasia"
                      labelColor={color}
                      {...field}
                      error={errors.fantasia}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="representante"
                  render={({ field }) => (
                    <InputText
                      label="Representante Legal"
                      labelColor={color}
                      {...field}
                      error={errors.representante}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="cpf_representante"
                  render={({ field }) => (
                    <InputText
                      label="CPF Repres."
                      labelColor={color}
                      mask="000.000.000-00"
                      {...field}
                      error={errors.cpf_representante}
                    />
                  )}
                />
              </SimpleGrid>
            </Flex>
          )}

          <SimpleGrid
            templateColumns={['1fr', '1fr', '1fr 1fr 2fr 2fr 2fr']}
            gap={4}
            justify="center"
            w="100%"
            mt="1"
          >
            <Controller
              control={control}
              name="telefone"
              render={({ field }) => (
                <InputText
                  label="Telefone"
                  labelColor={color}
                  mask="(00) 00000-0000"
                  {...field}
                  error={errors.telefone}
                />
              )}
            />

            <Controller
              control={control}
              name="celular"
              render={({ field }) => (
                <InputText
                  label="Celular"
                  labelColor={color}
                  mask="(00) 00000-0000"
                  {...field}
                  error={errors.celular}
                />
              )}
            />

            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <InputText
                  label="Email"
                  labelColor={color}
                  type="email"
                  autoComplete="off"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  data-lpignore="true"
                  data-form-type="other"
                  data-bwignore="true"
                  {...field}
                  name="certificado_email"
                  error={errors.email}
                />
              )}
            />

            <Controller
              control={control}
              name="email_contador"
              render={({ field }) => (
                <InputText
                  label="Email Contador"
                  labelColor={color}
                  type="email"
                  autoComplete="off"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  data-lpignore="true"
                  data-form-type="other"
                  data-bwignore="true"
                  {...field}
                  name="certificado_email_contador"
                  error={errors.email_contador}
                />
              )}
            />

            <Controller
              control={control}
              name="senha_emissao"
              render={({ field }) => (
                <InputText
                  label="Senha de Emissão"
                  labelColor={color}
                  type={mostrarSenhaEmissao ? 'text' : 'password'}
                  autoComplete="off"
                  placeholder="Opcional. Padrão: Sa@123456."
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  data-lpignore="true"
                  data-form-type="other"
                  data-bwignore="true"
                  {...field}
                  error={errors.senha_emissao}
                  inputRightAddon={
                    <InputRightAddon
                      cursor="pointer"
                      onClick={() =>
                        setMostrarSenhaEmissao(!mostrarSenhaEmissao)
                      }
                    >
                      <Tooltip
                        label={
                          mostrarSenhaEmissao
                            ? 'Ocultar senha'
                            : 'Visualizar senha'
                        }
                      >
                        {mostrarSenhaEmissao ? (
                          <EyeOff size={22} color={color} />
                        ) : (
                          <Eye size={22} color={color} />
                        )}
                      </Tooltip>
                    </InputRightAddon>
                  }
                />
              )}
            />

            <Controller
              control={control}
              name="confirmar_senha"
              render={({ field }) => (
                <InputText
                  label="Confirmar Senha"
                  labelColor={color}
                  type={mostrarConfirmacaoSenha ? 'text' : 'password'}
                  autoComplete="off"
                  placeholder="Digite a senha novamente"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  data-lpignore="true"
                  data-form-type="other"
                  data-bwignore="true"
                  {...field}
                  error={errors.confirmar_senha}
                  inputRightAddon={
                    <InputRightAddon
                      cursor="pointer"
                      onClick={() =>
                        setMostrarConfirmacaoSenha(!mostrarConfirmacaoSenha)
                      }
                    >
                      <Tooltip
                        label={
                          mostrarConfirmacaoSenha
                            ? 'Ocultar senha'
                            : 'Visualizar senha'
                        }
                      >
                        {mostrarConfirmacaoSenha ? (
                          <EyeOff size={22} color={color} />
                        ) : (
                          <Eye size={22} color={color} />
                        )}
                      </Tooltip>
                    </InputRightAddon>
                  }
                />
              )}
            />
          </SimpleGrid>

          <List
            spacing={1}
            mt="3"
            pl="5"
            fontSize="sm"
            color={color}
            styleType="disc"
          >
            <ListItem>Limite mínimo de 8 caracteres.</ListItem>
            <ListItem>Deve conter ao menos um número.</ListItem>
            <ListItem>Deve conter ao menos uma letra maiúscula.</ListItem>
            <ListItem>Deve conter ao menos um caractere especial.</ListItem>
            <ListItem>Senha e confirmação devem ser iguais.</ListItem>
          </List>

          <Flex my="4" rounded="2xl" h="2px" w="100%" bg={details} />

          <SimpleGrid
            templateColumns={['1fr', '1fr', '1fr 2fr 1fr 2fr']}
            gap={4}
            justify="center"
            w="100%"
          >
            <Controller
              control={control}
              name="cep"
              render={({ field }) => (
                <InputText
                  label="CEP"
                  labelColor={color}
                  mask="00.000-000"
                  {...field}
                  error={errors.cep}
                  borderLeftRadius="0"
                  rightIcon={<Search />}
                  inputRightAddon={
                    <InputRightAddon cursor="pointer" onClick={buscaDadosCep}>
                      {buscandoCEP ? (
                        <Tooltip label="Buscando dados do CEP">
                          <Spinner color={color} />
                        </Tooltip>
                      ) : (
                        <Tooltip label="Buscar dados do CEP">
                          <Search size={24} color={color} />
                        </Tooltip>
                      )}
                    </InputRightAddon>
                  }
                />
              )}
            />

            <Controller
              control={control}
              name="logradouro"
              render={({ field }) => (
                <InputText
                  label="Logradouro"
                  labelColor={color}
                  {...field}
                  error={errors.logradouro}
                />
              )}
            />

            <Controller
              control={control}
              name="numero"
              render={({ field }) => (
                <InputText
                  label="Nº"
                  labelColor={color}
                  {...field}
                  error={errors.numero}
                />
              )}
            />

            <Controller
              control={control}
              name="bairro"
              render={({ field }) => (
                <InputText
                  label="Bairro"
                  labelColor={color}
                  {...field}
                  error={errors.bairro}
                />
              )}
            />
          </SimpleGrid>

          <SimpleGrid
            templateColumns={['1fr', '1fr', '1fr 1fr 1fr 2fr']}
            gap={4}
            justify="center"
            w="100%"
            mt="1"
          >
            <Controller
              control={control}
              name="uf"
              render={({ field }) => (
                <InputText
                  label="UF"
                  labelColor={color}
                  {...field}
                  error={errors.uf}
                />
              )}
            />

            <Controller
              control={control}
              name="cidade"
              render={({ field }) => (
                <InputText
                  label="Cidade"
                  labelColor={color}
                  {...field}
                  error={errors.cidade}
                />
              )}
            />

            <Controller
              control={control}
              name="ibge"
              render={({ field }) => (
                <InputText
                  label="IBGE"
                  labelColor={color}
                  {...field}
                  error={errors.ibge}
                />
              )}
            />

            <Controller
              control={control}
              name="complemento"
              render={({ field }) => (
                <InputText
                  label="Complemento"
                  labelColor={color}
                  {...field}
                  error={errors.complemento}
                />
              )}
            />
          </SimpleGrid>

          <Flex my="4" rounded="2xl" h="2px" w="100%" bg={details} />

          <Flex mt="4">
            <Controller
              name="arquivos"
              control={control}
              render={({ field }) => (
                <FileUpload
                  savedFiles={certificadoData.arquivos}
                  onFilesChange={files => {
                    field.onChange(files);
                    setValue('arquivos', files, { shouldValidate: true });
                  }}
                  onError={errors?.arquivos}
                  maxTotalSize={MAX_TOTAL_FILES_SIZE}
                  onLimitExceeded={totalSize => {
                    Toast({
                      title: 'Arquivos muito grandes',
                      description: `O tamanho total dos arquivos e ${formatarTamanhoArquivo(
                        totalSize,
                      )}. O limite permitido e 7,5 MB.`,
                      isClosable: true,
                      status: 'error',
                      position: 'bottom-right',
                    });
                  }}
                />
              )}
            />
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};

export default DadosCertificado;
